import {
    IEmailDeliverabilityTestResponse,
    IInviteBookkeeperData,
    IParsedProAccessResponse,
    IUserResponse,
    SocialNetwork
} from '~/types/server.types';
import { trackError } from '~/services/logger/error.utils';
import {
    AccountRequestActions,
    TBookkeeperAccessActions
} from '~/models/account-access-request.model';
import { action } from 'mobx';
import { APP_HOME } from '~/common/url.constants';
import { User } from '~/models/user.model';
import { BookkeeperAccountAccessAuthorization } from '~/models/bookkeeper-access-request.model';
import type { GlobalMXContextValue } from '~/providers/mx-global-context/global-mx.context';
import { isReactFirst } from '~/utils/environment.utils';
import { loadUser } from '~/utils/user.utils';

export class UserController {
    globalContext: GlobalMXContextValue;

    constructor(globalContext: GlobalMXContextValue) {
        this.globalContext = globalContext;
    }

    updateAccountProperty(propertyData) {
        const {
            accountStore,
            userStore: {
                currUser: { account }
            }
        } = this.globalContext;
        const updatedAccount = Object.assign({}, account, propertyData);

        accountStore.update(updatedAccount);
    }

    @action.bound
    async requestAccessAccountFromMember(
        memberEmail: string,
        teamMemberIds: string[]
    ) {
        const { userService } = this.globalContext;
        const res = await userService.requestAccessFromUser(
            memberEmail,
            teamMemberIds
        );
        return res;
    }

    async getProAuthRequests(requestData: {
        statuses: string[] | undefined;
        expires_after?: Date;
        limit?: number;
        skip?: number;
        as_client: boolean;
    }): Promise<IParsedProAccessResponse> {
        const { userService } = this.globalContext;
        const formattedData = {
            ...requestData,
            statuses: requestData.statuses
                ? requestData.statuses.join(',')
                : undefined
        };

        const response = await userService.getProRequests(formattedData);
        return response;
    }

    async getBookkeeperAuthRequests(): Promise<
        BookkeeperAccountAccessAuthorization[]
    > {
        const { userService } = this.globalContext;

        try {
            const response = await userService.getBookkeeperRequests();

            const authInstances = response.results.map(request => {
                const requestInstance =
                    new BookkeeperAccountAccessAuthorization(request);
                return requestInstance;
            });

            return authInstances;
        } catch (e) {
            trackError(e);
            throw e;
        }
    }

    manageProAccountAccess = async (
        requestId: string,
        actionType: AccountRequestActions
    ) => {
        const { userService } = this.globalContext;
        const response = await userService.manageProAccountAccess(
            requestId,
            actionType
        );

        return response;
    };

    manageBookkeeperAccountAccess = async (
        requestId: string,
        actionType: TBookkeeperAccessActions
    ) => {
        const { userService } = this.globalContext;
        const response = await userService.manageBookkeeperAccountAccess(
            requestId,
            actionType
        );

        return response;
    };

    start2FAForProLogin = async () => {
        const { userService } = this.globalContext;
        const response = await userService.startPro2FA();
        return response;
    };

    proLoginAsVendor = async (userId: string, smsCode: string) => {
        const { userService, analyticsService, jStorageService } =
            this.globalContext;
        const userResponse = await userService.proLoginAsVendor({
            userId,
            smsCode,
            jStorageService
        });

        this.globalContext.sessionCtrl.localLogin(userResponse);

        // Initialize stores with account and company data
        const user = loadUser(userResponse, this.globalContext);

        analyticsService.identifyUser(user);

        setTimeout(() => {
            window.location.href = APP_HOME;
        }, 0);

        return userResponse;
    };

    async saveBookkeeper(data: IInviteBookkeeperData) {
        const { userService } = this.globalContext;
        const { company_name, ...bkprFields } = data;
        const dataToServer = {
            ...bkprFields,
            company: { company_name }
        };

        const response = await userService.saveBookkeeperUser(dataToServer);
        return new BookkeeperAccountAccessAuthorization(response);
    }

    async updatePhoneNumberForTwoAuth({
        phoneNumber
    }: {
        phoneNumber: string;
    }) {
        const { userService, userStore } = this.globalContext;
        await userService.updatePhoneNumberForTwoAuth({
            phoneNumber
        });
        const currUser = userStore.currUser;
        if (currUser) {
            userStore.update({
                _id: currUser._id,
                phone_number_for_two_factor_auth: phoneNumber.slice(-4)
            });
        }
    }

    async addUserTags(tags: object) {
        const { userService, userStore } = this.globalContext;
        await userService.addUserTags(userStore.currUser._id, { tags: tags });
    }

    updateCompanyForMWebQuiz({
        companyType,
        companyName,
        website,
        isFirstTypeUpdate
    }: {
        companyType?: string;
        companyName?: string;
        website?: string;
        isFirstTypeUpdate?: boolean;
    }) {
        const { userService, userStore } = this.globalContext;
        return userService.updateCompanyForMWebQuiz(userStore.currUser._id, {
            companyType,
            companyName,
            website,
            isFirstTypeUpdate
        });
    }

    async testEmailDeliverability() {
        const { userService } = this.globalContext;
        return userService.testEmailDeliverability();
    }

    async getEmailDeliverabilityStatus(): Promise<IEmailDeliverabilityTestResponse> {
        try {
            const { userService } = this.globalContext;
            return await userService.fetchEmailDeliverabilityTestStatus();
        } catch (error) {
            return {
                type: 'full',
                status: 'failed',
                test_errors: []
            };
        }
    }

    @action
    setProfileImg = (publicId: string, url: string) => {
        const { userStore } = this.globalContext;

        const currUser = userStore.currUser;

        const updatedUser = {
            ...currUser,
            profile_image: {
                ...currUser.profile_image,
                cloudinary_public_id: publicId,
                url: url,
                original_url: url
            }
        };

        userStore.update(updatedUser);
    };

    async connectSocialAccount({
        user,
        ...params
    }: {
        user: User;
        authToken: string;
        redirectUri?: string;
        socialNetworkType: Extract<SocialNetwork, 'facebook' | 'instagram'>;
    }) {
        try {
            const { userService } = this.globalContext;

            const response = await userService.connectSocialAccount({
                ...params,
                userId: user._id
            });
            this.updateUser(user, response);

            return response;
        } catch (error) {
            trackError({
                error,
                message: 'Error connecting social account',
                context: { social_network_type: params.socialNetworkType }
            });
            throw error;
        }
    }
    async disconnectSocialAccount({
        user,
        socialNetworkType
    }: {
        user: User;
        socialNetworkType: Extract<SocialNetwork, 'facebook' | 'instagram'>;
    }) {
        try {
            const { userService } = this.globalContext;

            const response = await userService.disconnectSocialAccount({
                socialNetworkType,
                userId: user._id
            });
            this.updateUser(user, response);

            return response;
        } catch (error) {
            trackError({
                error,
                message: 'Error disconnecting social account',
                context: { social_network_type: socialNetworkType }
            });
            throw error;
        }
    }

    updateUser(user: User, data: Partial<IUserResponse>) {
        // Hopefully temporary until this is merged https://github.com/HoneyBook/honeybook-react/pull/5809
        const { injectorService } = this.globalContext;
        Object.assign(user, data);

        if (isReactFirst()) {
            return;
        }
        const currUser = injectorService?.get('UsersManager')?.currUser;
        if (currUser) {
            Object.assign(currUser, data);
        }
    }
}
